import { IModel } from '@api/models/user/interface';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as actions from './actions';

interface IUser extends Omit<IModel, '_id' | 'createdAt'> {
  _id: string;
  email: string;
  avatar?: string;
  phone?: string;
  auth: {
    isInProgress: boolean;
    isLoggedIn: boolean;
  };
  createdAt: string;
}

const initialState: IUser = {
  _id: '',
  email: '',
  avatar: '',
  rank: 'spark',
  streakDays: 0,
  points: 0,
  totalPrayers: 0,
  prayedHubs: [],
  totalPrayerDuration: 0,
  savedPrayerHubs: [],
  phone: '',
  auth: {
    isInProgress: false,
    isLoggedIn: false,
  },
  createdAt: new Date().toISOString(),
};

export const user = createSlice({
  name: 'user',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<actions.Init>) => {
      state._id = action.payload._id;
      state.email = action.payload.email;
      state.name = action.payload.name;
      state.avatar = action.payload.avatar;
      state.auth.isLoggedIn = true;
    },
    updatePoints: (state, action: PayloadAction<number>) => {
      state.points += action.payload;
    },
    setAuthProgressState: (state, action: PayloadAction<boolean>) => {
      state.auth.isInProgress = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(actions.signIn.pending, state => {
      state.auth.isInProgress = true;
    });
    builder.addCase(actions.signIn.fulfilled, (state, action) => {
      state.auth.isInProgress = false;
      state.auth.isLoggedIn = true;

      if (action.payload.success) {
        state._id = action.payload.data._id.toString();
        state.email = action.payload.data.email;
        state.name = action.payload.data.name;
        state.avatar = action.payload.data.avatar;
        state.points = action.payload.data.points;
        state.totalPrayerDuration = action.payload.data.totalPrayerDuration;
        state.totalPrayers = action.payload.data.totalPrayers;
      }
    });
    builder.addCase(actions.signIn.rejected, state => {
      state.auth.isInProgress = false;
    });

    builder.addCase(actions.signOut.pending, state => {
      state.auth.isInProgress = true;
    });
    builder.addCase(actions.signOut.fulfilled, state => {
      state.auth.isInProgress = false;
      state.auth.isLoggedIn = false;
    });
    builder.addCase(actions.signOut.rejected, state => {
      state.auth.isInProgress = false;
    });

    builder.addCase(actions.me.pending, state => {
      state.auth.isInProgress = true;
    });
    builder.addCase(actions.me.fulfilled, (state, action) => {
      state.auth.isInProgress = false;

      if (action.payload.success) {
        state.auth.isLoggedIn = true;
        state._id = action.payload.data._id;
        state.email = action.payload.data.email;
        state.name = action.payload.data.name;
        state.avatar = action.payload.data.avatar;
        state.points = action.payload.data.points;
        state.totalPrayerDuration = action.payload.data.totalPrayerDuration;
        state.totalPrayers = action.payload.data.totalPrayers;
      } else {
        state.auth.isLoggedIn = false;
      }
    });
    builder.addCase(actions.me.rejected, state => {
      state.auth.isInProgress = false;
      state.auth.isLoggedIn = false;
    });
  },
});

export const { init, setAuthProgressState, updatePoints } = user.actions;
export default user.reducer;
